import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';

/**
 * ProtectedRoute — Guard de Autenticação e Autorização
 *
 * Verifica a sessão e a role de admin ANTES de renderizar (e baixar)
 * os chunks lazy das páginas administrativas.
 *
 * Fluxo:
 * 1. 'checking' → exibe null enquanto verifica a sessão
 * 2. 'unauthorized' → redireciona para /admin/login
 * 3. 'authorized' → renderiza os filhos (children)
 */
// Cache de sessão admin validada para evitar múltiplos roundtrips ao Supabase
let cachedAdminUserId = null;

export const invalidateAdminAuthCache = () => {
  cachedAdminUserId = null;
};

const ProtectedRoute = ({ children }) => {
  const [status, setStatus] = useState(() => cachedAdminUserId ? 'authorized' : 'checking');

  useEffect(() => {
    let isMounted = true;

    const verify = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          cachedAdminUserId = null;
          if (isMounted) setStatus('unauthorized');
          return;
        }

        // Se já validado na memória nesta sessão
        if (cachedAdminUserId === session.user.id) {
          if (isMounted) setStatus('authorized');
          return;
        }

        // 1. Verificar role via app_metadata (definido pelo service role — não adulterável pelo usuário)
        const appRole = session.user?.app_metadata?.role;
        if (appRole === 'admin') {
          cachedAdminUserId = session.user.id;
          if (isMounted) setStatus('authorized');
          return;
        }

        // 2. Verificar role via user_metadata (fallback)
        const userRole = session.user?.user_metadata?.role;
        if (userRole === 'admin') {
          cachedAdminUserId = session.user.id;
          if (isMounted) setStatus('authorized');
          return;
        }

        // 3. Verificar tabela admin_users no Supabase (método recomendado para produção)
        const { data, error } = await supabase
          .from('admin_users')
          .select('email')
          .eq('email', session.user.email)
          .maybeSingle();

        if (!error && data) {
          cachedAdminUserId = session.user.id;
          if (isMounted) setStatus('authorized');
          return;
        }

        // Usuário autenticado, mas sem permissão de admin
        cachedAdminUserId = null;
        await supabase.auth.signOut();
        if (isMounted) setStatus('unauthorized');
      } catch {
        cachedAdminUserId = null;
        if (isMounted) setStatus('unauthorized');
      }
    };

    verify();

    return () => {
      isMounted = false;
    };
  }, []);

  if (status === 'checking') {
    // Tela de espera minimalista enquanto verifica credenciais
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#0f172a',
        color: '#94a3b8',
        fontFamily: 'system-ui, sans-serif',
        fontSize: '0.875rem'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 28,
            height: 28,
            border: '2px solid #1e293b',
            borderTopColor: '#06b6d4',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px'
          }} />
          Verificando credenciais...
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (status === 'unauthorized') {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

export default ProtectedRoute;
